// Per-inspection video: list the recorded segment blocks with inline play +
// per-segment download. Read-only surface over the backend video pipeline —
// playback uses each segment's directly-playable CDN URL (public_url); download
// fetches a signed attachment URL. No recording/finalize/cursor UI here.

import { useEffect, useState } from 'react';
import { Box, Group, Text, UnstyledButton, Loader } from '@mantine/core';
import { IconChevronDown, IconPlayerPlay, IconDownload, IconVideo } from '@tabler/icons-react';
import { inspectionVideoApi, type InspectionVideo, type InspectionVideoSegment } from '../api/client';

const CARD_BG = '#18181B';
const BORDER = '#27272A';
const TEXT = '#F4F4F5';
const TEXT_MUTED = '#71717A';

function fmtDur(ms?: number | null): string {
  if (!ms || ms <= 0) return '';
  const s = Math.round(ms / 1000);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

// A recorded block is playable once its upload landed (the blob exists).
const isPlayable = (s: InspectionVideoSegment) =>
  !!s.public_url && (s.status === 'uploaded' || s.status === 'transcribed');

export function InspectionVideoSegments({ inspectionId }: { inspectionId: number }) {
  const [videos, setVideos] = useState<InspectionVideo[] | null>(null);
  const [open, setOpen] = useState(true);
  const [playingId, setPlayingId] = useState<number | null>(null);
  const [downloading, setDownloading] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setVideos(null);
    setPlayingId(null);
    inspectionVideoApi
      .listVideos(inspectionId)
      .then((v) => { if (!cancelled) setVideos(v); })
      .catch(() => { if (!cancelled) setVideos([]); });
    return () => { cancelled = true; };
  }, [inspectionId]);

  // Loading or nothing to show → render nothing (don't reserve space).
  if (videos === null) return null;
  const withSegs = videos
    .map((v) => ({ video: v, segs: (v.segments || []).filter(isPlayable) }))
    .filter((g) => g.segs.length > 0);
  if (withSegs.length === 0) return null;

  const totalSegs = withSegs.reduce((n, g) => n + g.segs.length, 0);

  const handleDownload = async (seg: InspectionVideoSegment) => {
    setDownloading(seg.id);
    try {
      const { download_url } = await inspectionVideoApi.segmentDownload(inspectionId, seg.id);
      if (download_url) {
        const a = document.createElement('a');
        a.href = download_url;
        a.target = '_blank';
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
    } catch {
      /* best-effort — leave the row as-is on failure */
    } finally {
      setDownloading(null);
    }
  };

  return (
    <Box style={{ background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: 10, overflow: 'hidden', marginBottom: 12 }}>
      <UnstyledButton
        onClick={() => setOpen((o) => !o)}
        style={{ width: '100%', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}
      >
        <Group gap={8}>
          <IconVideo size={16} color={TEXT_MUTED} />
          <Text size="sm" fw={700} c={TEXT}>Inspection video</Text>
          <Text size="xs" c={TEXT_MUTED}>{totalSegs} segment{totalSegs === 1 ? '' : 's'}</Text>
        </Group>
        <IconChevronDown size={15} color={TEXT_MUTED} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
      </UnstyledButton>

      {open && (
        <Box style={{ borderTop: `1px solid ${BORDER}` }}>
          {withSegs.map((g, gi) => (
            <Box key={g.video.id}>
              {withSegs.length > 1 && (
                <Text size="11px" c={TEXT_MUTED} fw={600} style={{ padding: '6px 14px 2px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Session {gi + 1}
                </Text>
              )}
              {g.segs.map((seg, i) => {
                const playing = playingId === seg.id;
                const dur = fmtDur(seg.duration_ms);
                return (
                  <Box key={seg.id} style={{ borderTop: i === 0 && gi === 0 ? 'none' : `1px solid ${BORDER}` }}>
                    <Group justify="space-between" wrap="nowrap" style={{ padding: '8px 14px' }}>
                      <Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
                        <Text size="sm" c={TEXT} fw={500}>Segment {seg.segment_index + 1}</Text>
                        {dur && <Text size="xs" c={TEXT_MUTED}>{dur}</Text>}
                      </Group>
                      <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
                        <UnstyledButton
                          onClick={() => setPlayingId(playing ? null : seg.id)}
                          title={playing ? 'Hide' : 'Play segment'}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, height: 28, padding: '0 10px', borderRadius: 6, border: `1px solid ${BORDER}`, color: playing ? '#22C55E' : TEXT, fontSize: 12, fontWeight: 600 }}
                        >
                          <IconPlayerPlay size={13} /> {playing ? 'Hide' : 'Play'}
                        </UnstyledButton>
                        <UnstyledButton
                          onClick={() => handleDownload(seg)}
                          title="Download segment"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, height: 28, padding: '0 10px', borderRadius: 6, border: `1px solid ${BORDER}`, color: TEXT_MUTED, fontSize: 12, fontWeight: 600 }}
                        >
                          {downloading === seg.id ? <Loader size={12} color="gray" /> : <IconDownload size={13} />} Download
                        </UnstyledButton>
                      </Group>
                    </Group>
                    {playing && seg.public_url && (
                      <Box style={{ padding: '0 14px 12px' }}>
                        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                        <video
                          src={seg.public_url}
                          controls
                          autoPlay
                          playsInline
                          style={{ width: '100%', maxHeight: '50vh', borderRadius: 8, background: '#000', display: 'block' }}
                        />
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
